<template>
  <div class="opc-input-number">
    <div
      @dragstart.prevent
      :class="[
        'zm-input-number',
        'opc-input-selectable',
        inputNumberSize ? 'zm-input-number--' + inputNumberSize : '',
        { 'is-disabled': inputNumberDisabled },
        { 'is-without-controls': !controls },
        { 'is-controls-right': controlsAtRight },
      ]"
    >
      <zm-popover v-if="!!decreaseTips" key='popper__decreaseTips_tips_zm' :placement="placement" width="320" trigger="hover">
        <p v-html="decreaseTips"></p>
        <span
          class="zm-input-number__decrease"
          role="button"
          key='popper__decrease'
          v-if="controls"
          data-testid="checkout-span-input-number-decrease"
          :aria-label="`${label} ${currentValue} Decrease`"
          :aria-valuenow="currentValue"
          v-repeat-click="decrease"
          :class="{ 'is-disabled': minDisabled || !!decreaseTips }"
          @keydown.enter="decrease"
          slot="reference"
        >
          <i :class="`zm-icon-${controlsAtRight ? 'down' : 'minus'}`"></i>
        </span>
      </zm-popover>
      <template v-else>
        <span
          class="zm-input-number__decrease"
          role="button"
          key='span__descrease'
          v-if="controls"
          data-testid="checkout-span-input-number-decrease"
          :aria-label="`${label} ${currentValue} Decrease`"
          :aria-valuenow="currentValue"
          v-repeat-click="decrease"
          :class="{ 'is-disabled': minDisabled }"
          @keydown.enter="decrease"
        >
          <i :class="`zm-icon-${controlsAtRight ? 'down' : 'minus'}`"></i>
        </span>
      </template>
      <zm-popover v-if="!!increaseTips" key='popper__increaseTips_tips_zm' :placement="placement" width="320" trigger="hover">
        <p style="word-break: break-word" v-html="increaseTips"></p>
        <span
          class="zm-input-number__increase"
          role="button"
          key='popper__increase'
          v-if="controls"
          data-testid="checkout-span-input-number-increase"
          :aria-label="`${label} ${currentValue} Increase`"
          :aria-valuenow="currentValue"
          v-repeat-click="increase"
          :class="{ 'is-disabled': maxDisabled || !!increaseTips }"
          slot="reference"
          @keydown.enter="increase"
        >
          <i :class="`zm-icon-${controlsAtRight ? 'up' : 'add'}`"></i>
        </span>
      </zm-popover>
      <template v-else>
        <span
          class="zm-input-number__increase"
          role="button"
          key='span__increase'
          v-if="controls"
          data-testid="checkout-span-input-number-increase"
          :aria-label="`${label} ${currentValue} Increase`"
          :aria-valuenow="currentValue"
          v-repeat-click="increase"
          :class="{ 'is-disabled': maxDisabled }"
          @keydown.enter="increase"
        >
          <i :class="`zm-icon-${controlsAtRight ? 'up' : 'add'}`"></i>
        </span>
      </template>
      <zm-input
        data-testid="checkout-input-28"
        ref="input"
        :type="!$context.is.desktop ? 'number' : 'text'"
        :pattern="!$context.is.desktop ? '[0-9]*' : null"
        :value="displayValue"
        :placeholder="placeholder"
        :disabled="inputNumberDisabled"
        :size="inputNumberSize"
        :max="max"
        :min="min"
        :name="name"
        :tabindex="tabindex"
        :label="disabledTips ? `${label} ${disabledTips}` : label"
        @keydown.up.native.prevent="increase"
        @keydown.down.native.prevent="decrease"
        @blur="handleBlur"
        @focus="handleFocus"
        @input="handleInput"
        @change="handleInputChange"
        data-1p-ignore
      />
    </div>
    <slot name="unit">
      <span v-if="unit" class="opc-input-number__unit">{{ unit }}</span>
    </slot>
    <slot name="tooltip">
      <zm-tooltip
        v-if="tooltip"
        class="opc-input-number__tooltip"
        :ariaLabel="tooltipLabel"
        :content="tooltip"
        placement="bottom"
      >
        <i class="zm-icon-info-outline" />
      </zm-tooltip>
    </slot>
  </div>
</template>

<script>
import InputNumber from '@zoom/zoom-ui/src/components/input-number';
import string, { isNumber } from 'lodash';

export default {
  name: 'input-number-with-popover',
  extends: InputNumber,
  props: {
    dataTracking: {
      type: Object,
      required: false,
    },
    increaseTips: {
      type: String,
      default: '',
    },
    decreaseTips: {
      type: String,
      default: '',
    },
    placement: {
      type: String,
      default: 'bottom',
    },
    precision: {
      type: Number,
      default: 0,
      validator(val) {
        return val >= 0 && val === parseInt(val, 10);
      },
    },
    unit: String,
    tooltip: String,
    tabindex: {
      type: Number,
      default: 0,
    }
  },
  methods: {
    handleInputChange(value) {
      if (value === '') value = this.currentValue;
      const newVal = value === '' ? undefined : Number(value);
      if (!isNaN(newVal) || value === '') {
        this.setCurrentValue(newVal);
      }
      this.userInput = null;
    },
  },
  computed: {
    disabledTips() {
      return this.increaseTips || this.decreaseTips;
    },
  },
  watch: {
    currentValue(newVal, oldVal) {
      if (this.dataTracking) {
        if (isNumber(newVal) && isNumber(oldVal)) {
          let count = newVal - oldVal;
          if (count === 0) {
            return;
          }
          let action = 'increase';
          if (count < 0) {
            action = 'decrease';
          }
          let plan = string.split(this.label, 'Select')[0] || '';
          const { buyInteraction = '', buySelection = '', planName = plan } = this.dataTracking
          this.$tracking.buyInteraction(
            buyInteraction,
            buySelection ? `${buySelection} ${planName} - ${action} - ${newVal}` : `${planName} - ${action} - ${newVal}`,
          );
        }
      }
    },
  },
};
</script>
<style lang="scss">
@import '@zoom/billing-responsive/styles/responsive.scss';
.opc-input-number {
  position: relative;
  display: inline-block;

  &__unit {
    margin-left: 8px;
    display: inline-block;

    &::first-letter {
      text-transform: uppercase;
    }
  }
  @include media(mobile, tablet) {
    .zm-input__inner[type='number'] {
      padding-right: 47px;

      -moz-appearance: textfield; /* Firefox */

      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
    }
  }
}

input[type='number']::-webkit-outer-spin-button,
input[type='number']::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type='number'] {
  -moz-appearance: textfield; /* Firefox */
}
</style>
